import EmptyState from 'components/EmptyState';
import Loader from 'components/Loader';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { useDispatch } from 'store';
import { useFindMatterRatesQuery, useGetMatterByIdQuery } from 'store/api/matters';
import { MatterForm } from 'types/matters';
import { infoAlert } from 'utils/alert';
import { useClassifiers } from 'utils/useClassifiers';

import { FetchBaseQueryError, skipToken } from '@reduxjs/toolkit/query';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import { EntityType } from 'types/entity';
import MatterPageForm from './MatterPageForm';
import { MATTER_FORM_CLASSIFIERS } from './form-sections/useMatterForm';
import { matterDtoToForm } from './matterFormMapping';
import { FocusedEditDeclined } from 'layout/MainLayout/focusedEdit';
import { withdrawFocusedEditDecline } from 'store/reducers/menu';
import { useSettings } from 'utils/useSettings';
import { Setting } from 'types/back-office-settings';

const ClassicMatterPageEdit = () => {
    const { id: matterId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isAdministratorFieldEnabled = useSettings().isSettingEnabled(Setting.ADMINISTRATOR_FIELD);
    const [initialData, setInitialData] = useState<MatterForm>();

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: MATTER_FORM_CLASSIFIERS,
    });

    const {
        data: matter,
        isError,
        isLoading,
        error: matterError,
    } = useGetMatterByIdQuery(matterId ?? skipToken, {
        refetchOnMountOrArgChange: true,
    });

    // TODO: effect-event-handler skip — reacts to async RTK Query error to navigate; no originating event.
    useEffect(() => {
        if (matterError) {
            const error = matterError as FetchBaseQueryError;
            if (error?.status === 'PARSING_ERROR' && error?.originalStatus === 403 && typeof error?.data === 'string') {
                infoAlert(error.data);
                navigate('/');
            }
        }
    }, [matterError, navigate]);

    // The edit form stages every list of the case and its save sends them all, so it starts from a fresh read
    // of the stored lists: a save built on a stale or failed read would delete lists nobody touched.
    const {
        data: pricelists,
        fulfilledTimeStamp: pricelistsFulfilledAt,
        isError: isPricelistsError,
        refetch: refetchPricelists,
    } = useFindMatterRatesQuery(matterId ?? skipToken, { refetchOnMountOrArgChange: true });
    // The failed read declined the focused-edit layout; a retry may bring the form after all, so the
    // route stands in for it again from the first frame of the reload.
    const retryPricelists = useCallback(() => {
        dispatch(withdrawFocusedEditDecline());
        refetchPricelists();
    }, [dispatch, refetchPricelists]);
    // A cache hit renders before the refetch the mount dispatches, so "fresh" means fulfilled after this page opened.
    const [openedAt] = useState(() => Date.now());
    const isPricelistsFresh = pricelistsFulfilledAt !== undefined && pricelistsFulfilledAt >= openedAt;

    const {
        customFields,
        initialCustomFieldsObject,
        isLoading: isLoadingCustomFieldData,
    } = useCustomFields({
        entityType: EntityType.MATTER,
        fields: matter?.customFields,
    });

    // Freeze the initial form snapshot once classifiers + custom fields finish loading.
    // Render-time guarded setState (https://react.dev/reference/react/useState#storing-information-from-previous-renders)
    // — `!initialData` prevents the loop, so later RTK refetches don't reset RHF state.
    // The snapshot also waits for the fresh read of the lists to land: frozen on a cached or failed read it
    // would hold lists the case no longer has, and the save would write that set over the stored one.
    const isPricelistsUnavailable = isPricelistsError && !initialData;
    if (
        !initialData &&
        !isLoadingClassifiers &&
        !isLoadingCustomFieldData &&
        isPricelistsFresh &&
        !isPricelistsError &&
        pricelists &&
        matter
    ) {
        setInitialData(
            matterDtoToForm(matter, {
                classifiers,
                pricelists,
                customFieldValues: initialCustomFieldsObject,
                isAdministratorFieldEnabled,
            })
        );
    }

    if (
        isLoading ||
        isLoadingClassifiers ||
        (initialData === undefined && !isError && !isPricelistsUnavailable) ||
        isLoadingCustomFieldData
    ) {
        return <Loader />;
    }

    if (matterError) {
        return <FocusedEditDeclined />;
    }

    if (isPricelistsUnavailable) {
        return (
            <>
                <FocusedEditDeclined />
                <EmptyState
                    variant="error"
                    errorTitleId="matters.pricelists-unavailable-title"
                    errorBodyId="matters.pricelists-unavailable-body"
                    onRetry={retryPricelists}
                    surface="matter-edit-pricelists"
                />
            </>
        );
    }

    return (
        <MatterPageForm
            type="edit"
            initialData={initialData}
            classifiers={classifiers}
            currencyAccessible={matter?.currencyAccessible}
            hasConnectedInvoices={matter?.hasConnectedInvoices}
            matter={matter}
            entityCustomFields={customFields}
        />
    );
};

/** In the new design a case is edited section by section on its page, so its edit URL opens Case details there. */
const MatterPageEdit = () => {
    const isNew = useIsNewDesign();
    const { id: matterId } = useParams();
    return isNew ? <Navigate to={`/matter/${matterId}?edit=details`} replace /> : <ClassicMatterPageEdit />;
};

export default MatterPageEdit;
